import { useQueryClient } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";

import { standSeedKey, useCase, useLatestCalculation, type CaseStatus } from "@/api";
import { facadeEditability } from "@/lib/facadeEditability";

import { Button } from "./Button";
import styles from "./ChangeFormDialog.module.css";
import { releaseLossSentence } from "./ConfirmReleaseLoss";
import { Dialog, DialogFooter } from "./Dialog";
import { DisputedFigureNotice, useDisputedFindings } from "./DisputedFigureNotice";

/** What changing the form costs (spec 2026-10-15 §3), word for word. */
export const CHANGE_FORM_TEXT =
  "Die Form wird neu gezeichnet. Roste, Objekte, Ausschnitte, Ecken und Konsolen dieses Vorgangs werden danach entfernt — du belegst neu (‚Alle Wände belegen‘). Maße bleiben erhalten, soweit die Wand bleibt.";

/**
 * And what it does not cost: the Vorgang's own settings survive a Basisstand
 * (`PlanningStateIn.for_new_form`, Task 5 ruling 1). Said, because
 * „Konsolen … werden entfernt“ alone reads as if the console settings went
 * too. The four a person sets on the board; the cutout surcharge rate stays as
 * well, but it is not set there.
 */
export const CHANGE_FORM_KEEPS =
  "Die Einstellungen des Vorgangs bleiben: Konsolentyp, Konsolenserie, Aufteilung und Montagespiel.";

/**
 * What the dialog says when the plan holds a wall whose figure the sketch
 * contradicts (E-64, R-17): the editor draws that wall at its typed figure,
 * the board at the sketch's length — so the figure is settled there first.
 */
export const DISPUTED_FIGURE_TITLE = "Erst das Maß klären";
export const DISPUTED_FIGURE_TEXT =
  "„Form ändern“ zeichnet diese Wand mit der getippten Zahl — klär das Maß auf der Planung („1,75 m übernehmen“, „Stimmt so“ oder „Maß eintippen“).";

/** Where the planning lands after a Basisstand (`?form=neu`) — read by the board, written by the editor. */
export const FORM_CHANGED_PARAM = "form";
export const FORM_CHANGED_VALUE = "neu";
/** What the board says over its empty surface then. */
export const FORM_CHANGED = "Form geändert — neu belegen.";

/** The outline editor seeded from the Vorgang's latest Stand. */
export function changeFormPath(caseId: string): string {
  return `/umriss?case=${encodeURIComponent(caseId)}&aus=stand`;
}

/** The board of the Vorgang, as a Basisstand leaves it. */
export function formChangedPath(caseId: string): string {
  return `/vorgang/${caseId}/planung?${FORM_CHANGED_PARAM}=${FORM_CHANGED_VALUE}`;
}

interface ChangeFormDialogProps {
  /**
   * Which Vorgang this is about — to read its disputed figures off the plan
   * (`useDisputedFindings`). Without it the dialog asks for nothing.
   */
  caseId?: string | null;
  caseNo?: string | null;
  title?: string | null;
  /**
   * The release this change would end — `null` on a Vorgang that is not
   * released. `by`/`at` `null` where they are not known (yet): the question
   * is asked all the same (`releaseLossSentence`).
   */
  release: { by: string | null; at: string | null } | null;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * The question before „Form ändern …“ (spec 2026-10-15 §3, §4): what goes,
 * what stays — and on a released Vorgang, first of all, the release. One
 * dialog and not `ConfirmReleaseLoss` before it: two questions in a row for
 * one gesture would be one too many, and the release sentence is the same one
 * (`releaseLossSentence`).
 *
 * The focus lands on „Abbrechen“, as on `ConfirmReleaseLoss`: it stands first
 * in the footer, and the safe way out is the one an Enter pressed out of habit
 * should take.
 */
export function ChangeFormDialog({
  caseId = null,
  caseNo = null,
  title = null,
  release,
  onConfirm,
  onCancel,
}: ChangeFormDialogProps) {
  // The plan is the board's cached answer — asked only while this question is open.
  const disputed = useDisputedFindings(caseId);
  const subtitle =
    caseNo !== null ? (
      <>
        Vorgang <span className="num">{caseNo}</span>
        {title !== null ? ` · ${title}` : null}
      </>
    ) : undefined;
  return (
    <Dialog title="Form ändern" subtitle={subtitle} onClose={onCancel}>
      {release !== null ? <p className={styles.release}>{releaseLossSentence(release.by, release.at)}</p> : null}
      <p className={styles.text}>{CHANGE_FORM_TEXT}</p>
      <p className={styles.keeps}>{CHANGE_FORM_KEEPS}</p>
      {/* Said, not held: the person may still want to change the shape. */}
      {disputed.length > 0 ? (
        <DisputedFigureNotice title={DISPUTED_FIGURE_TITLE} findings={disputed} closing={DISPUTED_FIGURE_TEXT} />
      ) : null}
      <DialogFooter>
        <Button onClick={onCancel}>Abbrechen</Button>
        <Button variant="primary" onClick={onConfirm}>
          Form ändern
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

interface ChangeFormFacts {
  status: CaseStatus;
  latestVersionNo: number;
  caseNo: string;
  title: string;
}

/**
 * „Form ändern …“ as a gesture of its own (Task 9): whether it is offered,
 * what opens its question, and the question itself — for a caller that offers
 * it as a button (`ChangeFormButton`, the board's head) or as an item of the
 * Vorgang's „Mehr …“ (the frame's head, the record's).
 *
 * One place for the rule (spec 2026-10-15 §3): **offered** while the facade
 * may still change (`facadeEditability` open or approved) **and** there is a
 * Stand whose form could change. From „Angebot raus“ on and on a cancelled
 * Vorgang it is not offered at all.
 *
 * „Form ändern“ in the dialog leads to the editor seeded from the Stand —
 * after throwing away a seed cached from an earlier visit, so the editor
 * always begins from the Stand as it is now, never from one a measure
 * gesture has since moved past.
 */
export function useChangeForm(
  caseId: string,
  /**
   * What the caller already holds about the Vorgang — the record and the
   * frame have it on their own answer. Without it the gesture reads the
   * Vorgang off its cache (`useCase`), which `CaseRoute` filled before the
   * board was shown.
   */
  known?: ChangeFormFacts,
): { offered: boolean; ask: () => void; dialog: ReactNode } {
  const navigate = useNavigate();
  const client = useQueryClient();
  const [asking, setAsking] = useState(false);
  const cached = useCase(known === undefined ? caseId : null).data;
  const facts =
    known ??
    (cached !== undefined
      ? { status: cached.status, latestVersionNo: cached.latestVersionNo, caseNo: cached.caseNo, title: cached.title }
      : null);
  const editability = facts !== null ? facadeEditability(facts.status) : null;
  const offered = facts !== null && facts.latestVersionNo > 0 && (editability === "open" || editability === "approved");
  // Asked for as soon as the gesture is offered, not when the dialog opens:
  // the question should name the release from its first frame, not change
  // under the reader's eyes.
  const latest = useLatestCalculation(offered && editability === "approved" ? caseId : null);

  const review = latest.data?.review ?? null;
  const release =
    editability !== "approved"
      ? null
      : review !== null && review.kind !== "withdrawn"
        ? { by: review.by, at: review.at }
        : { by: null, at: null };

  const dialog =
    offered && asking && facts !== null ? (
      <ChangeFormDialog
        caseId={caseId}
        caseNo={facts.caseNo}
        title={facts.title}
        release={release}
        onCancel={() => setAsking(false)}
        onConfirm={() => {
          client.removeQueries({ queryKey: standSeedKey(caseId) });
          setAsking(false);
          navigate(changeFormPath(caseId));
        }}
      />
    ) : null;

  return { offered, ask: () => setAsking(true), dialog };
}

interface ChangeFormButtonProps {
  caseId: string;
  /** What the caller already holds about the Vorgang (`useChangeForm`). */
  known?: ChangeFormFacts;
  className?: string;
}

/**
 * „Form ändern …“ as a button — beside the walls it changes, on the board
 * (spec 2026-10-15 §3). A shared block because the screens may not import
 * each other (R-W1); the rule is `useChangeForm`'s.
 */
export function ChangeFormButton({ caseId, known, className }: ChangeFormButtonProps) {
  const { offered, ask, dialog } = useChangeForm(caseId, known);
  if (!offered) return null;
  return (
    <>
      <Button className={className} onClick={ask}>
        Form ändern …
      </Button>
      {dialog}
    </>
  );
}
